import {
  Button,
  Form,
  Image,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import SmsApi, { Advertise } from 'apis/sms';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import i18n from 'i18n';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { useCallback, useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { CMS_LANGUAGE_OPTION_LIST } from 'constant/language';

export const typeOptionList = [
  {
    value: 0,
    label: i18n.t('pc_home_page_carousel'),
  },
  {
    value: 1,
    label: i18n.t('app_home_carousel'),
  },
];

export const statusOptionList = [
  {
    value: 1,
    label: i18n.t('online'),
  },
  {
    value: 0,
    label: i18n.t('offline'),
  },
];

const useAdListQueryParams = () => {
  const [pageNum, setPageNum] = useQueryState(
    'pageNum',
    parseAsInteger.withDefault(1),
  );
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(10),
  );
  const [keyword, setKeyword] = useQueryState(
    'keyword',
    parseAsString.withDefault(''),
  );
  const [type, setType] = useQueryState(
    'type',
    parseAsArrayOf(parseAsInteger).withDefault([]),
  );
  const [langList, setLangList] = useQueryState(
    'langList',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [status, setStatus] = useQueryState('status', parseAsInteger);

  return {
    pageNum,
    pageSize,
    keyword,
    type,
    langList,
    status,
    setPageNum,
    setPageSize,
    setKeyword,
    setType,
    setLangList,
    setStatus,
  };
};

const ADList = () => {
  const navigate = useNavigate();
  const {
    pageNum,
    pageSize,
    keyword,
    type,
    langList,
    status,
    setPageNum,
    setPageSize,
    setKeyword,
    setType,
    setLangList,
    setStatus,
  } = useAdListQueryParams();

  const { loading, setLoading, total, setTotal, dataSource, setDataSource } =
    usePagination<Advertise>();

  const [form] = Form.useForm<{
    keyword?: string;
    langList?: string[];
    type?: number[];
    status?: number;
  }>();

  const getDataSource = useCallback(async () => {
    try {
      setLoading(true);
      const { list, total } = await SmsApi.getADList({
        pageNum,
        pageSize,
        keyword: keyword || undefined,
        type: type.length ? type : undefined,
        langList: langList.length ? langList.join(',') : undefined,
        status: status ?? undefined,
      });

      setDataSource(list);
      setTotal(total);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [
    keyword,
    langList,
    pageNum,
    pageSize,
    setDataSource,
    setLoading,
    setTotal,
    status,
    type,
  ]);

  useEffect(() => {
    form.setFieldsValue({
      keyword,
      type,
      langList,
      status: status ?? undefined,
    });
  }, [form, keyword, langList, status, type]);

  useEffect(() => {
    getDataSource();
  }, [getDataSource]);

  const onFinish = useCallback(
    (values: {
      keyword?: string;
      langList?: string[];
      type?: number[];
      status?: number;
    }) => {
      setKeyword(values.keyword || '');
      setType(values.type?.length ? values.type : null);
      setLangList(values.langList?.length ? values.langList : null);
      setStatus(values.status ?? null);
      setPageNum(1);
    },
    [setKeyword, setLangList, setPageNum, setStatus, setType],
  );

  const onReset = useCallback(() => {
    setKeyword('');
    setType(null);
    setLangList(null);
    setStatus(null);
    setPageNum(1);
    setPageSize(10);
  }, [setKeyword, setLangList, setPageNum, setPageSize, setStatus, setType]);

  const columns: ColumnsType<Advertise> = useMemo(() => {
    return [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
      },
      {
        title: i18n.t('ad_name'),
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: i18n.t('ad_placement'),
        dataIndex: 'type',
        key: 'type',
        render: (type: Advertise['type']) =>
          typeOptionList.find((d) => d.value === type)?.label,
      },
      {
        title: i18n.t('language'),
        dataIndex: 'language',
        key: 'language',
        render: (language: string) => {
          return (
            CMS_LANGUAGE_OPTION_LIST.find((i) => i.value === language)?.label ||
            '-'
          );
        },
      },
      {
        title: i18n.t('ad_image'),
        dataIndex: 'pic',
        key: 'pic',
        render: (pic?: string) => {
          if (!pic) return '-';
          return <Image src={pic} className="w-24" />;
        },
      },
      {
        title: i18n.t('time'),
        key: 'time',
        width: 200,
        render: (record: Advertise) => {
          return (
            <div>
              <div>
                <div>{i18n.t('start_time')}：</div>
                {dayjs(record?.startTime).format('YYYY-MM-DD HH:mm:ss')}
              </div>
              <div>
                <div>{i18n.t('end_time')}：</div>
                {dayjs(record?.endTime).format('YYYY-MM-DD HH:mm:ss')}
              </div>
            </div>
          );
        },
      },
      {
        title: i18n.t('online_offline'),
        dataIndex: 'status',
        key: 'status',
        render: (status: 0 | 1 | null, record: Advertise) => {
          return (
            <Switch
              checked={status === 1}
              onChange={async (checked) => {
                await SmsApi.AdEditService({
                  ...record,
                  status: checked ? 1 : 0,
                });
                getDataSource();
              }}
            ></Switch>
          );
        },
      },
      {
        key: 'options',
        title: <Trans i18nKey={LOCALS.options} />,
        fixed: 'right',
        width: 220,
        render: (record: Advertise) => {
          const id = record.id;
          return (
            <div>
              <Button
                type="link"
                onClick={() => {
                  navigate(`/sms/ad-detail?id=${id}`);
                }}
              >
                <Trans i18nKey={LOCALS.edit} />
              </Button>

              <Popconfirm
                title={i18n.t('delete')}
                description={
                  <div className="w-[150px]">
                    {i18n.t('are_you_sure_to_delete')}
                  </div>
                }
                onConfirm={async () => {
                  await SmsApi.AdDelete(id);
                  getDataSource();
                }}
              >
                <Button type="link" danger>
                  <Trans i18nKey={LOCALS.delete} />
                </Button>
              </Popconfirm>
            </div>
          );
        },
      },
    ];
  }, [getDataSource, navigate]);

  return (
    <div>
      <Form form={form} onFinish={onFinish} layout="inline">
        <Form.Item name="keyword" label={<Trans i18nKey={LOCALS.keyword} />}>
          <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
        </Form.Item>

        <Form.Item name="type" label={i18n.t('ad_placement')} className="w-60">
          <Select
            mode="multiple"
            placeholder={i18n.t(LOCALS.please_select) || ''}
            style={{ minWidth: 120 }}
            options={typeOptionList}
          />
        </Form.Item>

        <Form.Item name="langList" label={i18n.t('language')}>
          <Select
            mode="multiple"
            placeholder={i18n.t(LOCALS.please_select) || ''}
            style={{ minWidth: 120 }}
            options={CMS_LANGUAGE_OPTION_LIST.map(({ value, label }) => {
              return {
                value,
                label,
              };
            })}
          />
        </Form.Item>

        <Form.Item name="status" label={i18n.t('online_offline')}>
          <Select
            allowClear
            placeholder={i18n.t(LOCALS.please_select) || ''}
            style={{ minWidth: 120 }}
            options={statusOptionList}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              <Trans i18nKey={LOCALS.search} />
            </Button>

            <Button htmlType="button" onClick={onReset}>
              <Trans i18nKey={LOCALS.reset} />
            </Button>
            <Button
              onClick={() => {
                navigate('/sms/ad-detail');
              }}
            >
              <Trans i18nKey={LOCALS.add} />
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        tableLayout="fixed"
        bordered
        pagination={{
          total,
          pageSize,
          current: pageNum,
          onChange: (page, nextPageSize) => {
            setPageNum(page);
            setPageSize(nextPageSize);
          },
        }}
        loading={loading}
        rowKey={'id'}
        style={{
          marginTop: 12,
        }}
        dataSource={dataSource}
        columns={columns}
      />
    </div>
  );
};

export default ADList;
